import { useRef, useState } from 'react'
import { compressImageForStorage } from '../../services/imageCompress'
import { toast } from './Toast'
import { useLocale } from '../../i18n/context'
import { isImageContent } from '../../utils/imageContent'

const MAX_RAW_FILE_MB = 20

interface Props {
  content: string
  onChange: (content: string) => void
}

/**
 * Unified image-side input widget. Handles empty state (file picker + URL entry)
 * and filled state (thumbnail + replace + URL edit). Compression is handled
 * internally so callers only deal with the resulting data URL or HTTPS URL string.
 */
export default function ImageSideInput({ content, onChange }: Props) {
  const { tr } = useLocale()
  const fileRef = useRef<HTMLInputElement>(null)
  const [showUrl, setShowUrl] = useState(false)
  const [urlDraft, setUrlDraft] = useState('')

  const hasImage = isImageContent(content)
  const isDataUrl = content.startsWith('data:image/')

  async function handleFile(file: File) {
    if (file.size > MAX_RAW_FILE_MB * 1024 * 1024) {
      toast.error(tr.sideInput.errImageTooLarge(MAX_RAW_FILE_MB))
      return
    }
    try {
      const dataUrl = await compressImageForStorage(file)
      onChange(dataUrl)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : tr.sideInput.errCouldNotProcessImage)
    }
  }

  function commitUrl() {
    const url = urlDraft.trim()
    if (!url) return
    onChange(url)
    setUrlDraft('')
    setShowUrl(false)
  }

  function openUrlPanel() {
    setUrlDraft(content)
    setShowUrl(true)
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={async (e) => {
          const file = e.target.files?.[0]
          if (file) await handleFile(file)
          e.target.value = ''
        }}
      />

      {hasImage ? (
        <div style={{
          background: 'var(--bg-secondary)',
          borderRadius: 'var(--radius-sm)',
          border: '1px solid var(--border)',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 72,
          maxHeight: 180,
        }}>
          <img
            src={content}
            alt=""
            style={{ maxWidth: '100%', maxHeight: 180, objectFit: 'contain', display: 'block' }}
            onError={(e) => {
              const el = e.currentTarget
              el.style.display = 'none'
              const msg = el.nextElementSibling as HTMLElement | null
              if (msg) msg.style.display = 'block'
            }}
          />
          <span style={{ display: 'none', fontSize: 11, color: 'var(--text-secondary)', padding: 10 }}>
            {tr.sideInput.couldNotLoadImage}
          </span>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          style={{
            padding: '14px 0',
            background: 'var(--bg-secondary)',
            border: '1.5px dashed var(--border)',
            borderRadius: 'var(--radius-sm)',
            color: 'var(--text-secondary)',
            fontSize: 12,
            cursor: 'pointer',
            textAlign: 'center',
            transition: 'border-color 0.15s, color 0.15s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)' }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-secondary)' }}
        >
          {tr.sideInput.clickToChooseImage}
          <div style={{ fontSize: 10, marginTop: 3, opacity: 0.65 }}>JPG, PNG, GIF, WebP, SVG… {/* i18n-exempt: format names */}</div>
        </button>
      )}

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {hasImage ? (
          <>
            <button type="button" onClick={() => fileRef.current?.click()} style={pillBtn}>{tr.sideInput.replace}</button>
            {!isDataUrl && (
              <button type="button" onClick={() => showUrl ? setShowUrl(false) : openUrlPanel()} style={pillBtn}>
                {showUrl ? tr.sideInput.cancel : tr.sideInput.editUrl}
              </button>
            )}
          </>
        ) : (
          <>
            <button type="button" onClick={() => fileRef.current?.click()} style={pillBtn}>{tr.sideInput.uploadFile}</button>
            <button type="button" onClick={() => showUrl ? setShowUrl(false) : openUrlPanel()} style={pillBtn}>
              {showUrl ? tr.sideInput.cancel : tr.sideInput.fromUrl}
            </button>
          </>
        )}
      </div>

      {showUrl && (
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            type="url"
            value={urlDraft}
            autoFocus
            placeholder="https://example.com/image.png" // i18n-exempt: URL example, not language content
            onChange={(e) => setUrlDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commitUrl() }
              if (e.key === 'Escape') setShowUrl(false)
            }}
            style={{
              flex: 1,
              padding: '5px 8px',
              fontSize: 12,
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--text-primary)',
              outline: 'none',
              fontFamily: 'var(--font-ui)',
            }}
            onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--accent)')}
            onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
          />
          <button
            type="button"
            onClick={commitUrl}
            style={{ ...pillBtn, background: 'var(--accent-fill)', color: 'var(--text-on-accent)', borderColor: 'var(--accent-fill)' }}
          >
            {hasImage ? tr.sideInput.save : tr.sideInput.add}
          </button>
          <button type="button" onClick={() => setShowUrl(false)} style={pillBtn}>✕</button>
        </div>
      )}
    </div>
  )
}

const pillBtn: React.CSSProperties = {
  padding: '4px 10px',
  background: 'var(--bg-secondary)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-pill)',
  fontSize: 12,
  fontWeight: 500,
  cursor: 'pointer',
  color: 'var(--text-secondary)',
  fontFamily: 'var(--font-ui)',
}
